import { ArrowLeft, ArrowRight } from '@mos-cat/ds-icons';
import { pxToRem } from '@mos-cat/ds-theme';
import { ReactElement, HTMLAttributes } from 'react';
import flattenChildren from 'react-keyed-flatten-children';
import { createComponent, createHook } from 'reakit-system';

import { Crumb, CrumbProps } from './Crumb';
import { Box, useBox } from '../Box/Box';
import { StylesOptions, useStyles } from '../hooks/useStyles';
import { useRequiredPropWarning } from '../hooks/useRequiredPropWarning';
import { ecss, cx } from '../reexports/emotion';

export { Crumb, CrumbProps };

type Nullish = null | undefined | false;

/**
 * @beta
 */
type BreadcrumbsHTMLProps = HTMLAttributes<HTMLElement>;
type BreadcrumbsOptions = StylesOptions & {
  baseCrumbIndex?: number;
  children: (ReactElement<CrumbProps, typeof Crumb> | Nullish)[];
};

export type BreadcrumbsProps = BreadcrumbsOptions & BreadcrumbsHTMLProps;

const useBreadcrumbs = createHook<BreadcrumbsOptions, BreadcrumbsHTMLProps>({
  name: 'Breadcrumbs',
  keys: ['baseCrumbIndex', 'children'],
  compose: [useBox, useStyles],
  useOptions(options) {
    return {
      ...options,
      py: 'sp-8',
    };
  },
  useProps({ baseCrumbIndex, children }, { ...props }) {
    const breadcrumbsContent = flattenChildren(children) as (
      | ReactElement<CrumbProps, typeof Crumb>
      | Nullish
    )[];
    const { length } = breadcrumbsContent.filter(Boolean);
    useRequiredPropWarning(Boolean(children), 'Breadcrumbs', 'children');
    const breadcrumbsChildren = breadcrumbsContent.map((child, index) => {
      if (!child) return null;
      const prevChild = index === length - 2;
      const lastChild = index === length - 1;
      const baseCrumb =
        baseCrumbIndex !== undefined ? index === baseCrumbIndex : prevChild;

      return (
        <Box
          as="li"
          color="breadcrumbsColor"
          alignItems="center"
          // eslint-disable-next-line react/no-array-index-key -- нет другого параметра, на который можно было бы опереться
          key={index}
          flexShrink={child.props.noShrink ? 0 : 1}
          display={baseCrumb ? 'flex' : ['none', 'flex']}
          minWidth={0}
          m="sp-0"
        >
          <Box
            as={ArrowLeft}
            display={['block', 'none']}
            mr={pxToRem(6)}
            flexGrow={0}
            flexShrink={0}
            size={pxToRem(18)}
          />
          {child}
          {!lastChild && (
            <Box
              as={ArrowRight}
              display={['none', 'block']}
              ml={pxToRem(6)}
              mr="sp-4"
              flexGrow={0}
              flexShrink={0}
              size={pxToRem(18)}
            />
          )}
        </Box>
      );
    });
    return {
      'aria-label': 'Навигационная цепочка',
      ...props,
      className: cx(
        props.className,
        ecss({
          label: 'Breadcrumbs',
        })
      ),
      children: (
        <Box as="ol" display="flex" flexWrap="nowrap" p={0}>
          {breadcrumbsChildren}
        </Box>
      ),
    };
  },
});

/**
 * @beta
 */
export const Breadcrumbs = createComponent({
  as: 'nav',
  useHook: useBreadcrumbs,
  memo: true,
});
